import { zodResolver } from '@hookform/resolvers/zod'
import { Check, Loader2, Plus, Tags } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { PageHeader } from '@/components/common/PageHeader'
import { ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { useCategories, useDeleteCategory, useSaveCategory, useTransactions } from './api'
import { CategoryDot, Section, VatStatusBadge } from './components/FinanceBits'
import { FINANCE_CATEGORY_PERM } from './permissions'
import { TX_KINDS, VAT_STATUSES, type Category, type TxKind } from './types'
import { toastError } from './ui'
import { chartColor } from './visuals'
import { CheckRow, Field, FormSection } from './components/FormBits'

export function CategoriesPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const categories = useCategories()
  const txs = useTransactions()
  const remove = useDeleteCategory()
  const [form, setForm] = useState<{ open: boolean; category: Category | null; kind: TxKind }>({ open: false, category: null, kind: 'expense' })
  const canWrite = can(FINANCE_CATEGORY_PERM)
  const usage = (id: string) => (txs.data ?? []).filter((x) => x.categoryId === id).length

  const onDelete = async (c: Category) => {
    if (!(await confirmModal({ title: t('finance.categories.deleteTitle'), description: t('finance.categories.deleteBody', { name: c.name }), confirmLabel: t('common.delete'), destructive: true }))) return
    try {
      await remove.mutateAsync(c.id)
      toast.success(t('finance.categories.deleted'))
    } catch (e) {
      if (isApiError(e) && e.code === 'CATEGORY_IN_USE') toast.error(t('errors.CATEGORY_IN_USE', { count: (e.details as { count?: number } | undefined)?.count ?? 0 }))
      else toastError(e)
    }
  }

  return (
    <>
      <PageHeader eyebrow={t('nav.groups.finance')} title={t('finance.categories.title')} description={t('finance.categories.subtitle')} />
      {categories.isError ? (
        <ErrorState error={categories.error} onRetry={() => void categories.refetch()} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {(['expense', 'income'] as const).map((kind) => {
            const list = (categories.data ?? []).filter((c) => c.kind === kind)
            return (
              <Section
                key={kind}
                title={t(`finance.categories.${kind}`)}
                icon={Tags}
                bodyClassName="p-0"
                actions={
                  canWrite ? (
                    <Button size="sm" variant="outline" onClick={() => setForm({ open: true, category: null, kind })}>
                      <Plus />
                      {t('common.add')}
                    </Button>
                  ) : null
                }
              >
                {categories.isLoading ? (
                  <div className="space-y-2 p-4">
                    <Skeleton className="h-10" />
                    <Skeleton className="h-10" />
                  </div>
                ) : (
                  <ul className="divide-y">
                    {list.map((c) => (
                      <li key={c.id} className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3', !c.active && 'opacity-60')}>
                        <CategoryDot color={c.color} />
                        <div className="min-w-0 flex-1 basis-40">
                          <p className="truncate font-medium">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{t('finance.categories.usage', { count: usage(c.id) })}</p>
                        </div>
                        {kind === 'expense' ? <VatStatusBadge status={c.vatStatus} /> : null}
                        {!c.active ? <Badge tone="neutral">{t('finance.passive')}</Badge> : null}
                        {canWrite ? (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setForm({ open: true, category: c, kind })}>
                              {t('common.edit')}
                            </Button>
                            <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => void onDelete(c)}>
                              {t('common.delete')}
                            </Button>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            )
          })}
        </div>
      )}
      {form.open ? <CategoryForm key={form.category?.id ?? `new-${form.kind}`} category={form.category} kind={form.kind} onDone={() => setForm((s) => ({ ...s, open: false }))} /> : null}
    </>
  )
}

function CategoryForm({ category, kind, onDone }: { category: Category | null; kind: TxKind; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveCategory()
  const schema = z.object({
    name: z.string().trim().min(2, t('finance.categories.nameRequired')).max(40),
    kind: z.enum(TX_KINDS),
    vatStatus: z.enum(VAT_STATUSES),
    color: z.number().int().min(1).max(5),
    active: z.boolean(),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { name: category?.name ?? '', kind: category?.kind ?? kind, vatStatus: category?.vatStatus ?? 'eligible', color: category?.color ?? 1, active: category?.active ?? true } })
  const { errors } = form.formState
  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await save.mutateAsync({ id: category?.id, input })
      toast.success(t(category ? 'finance.categories.updated' : 'finance.categories.created'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.code === 'CATEGORY_NAME_TAKEN') form.setError('name', { message: t('errors.CATEGORY_NAME_TAKEN') })
      else toastError(e)
    }
  })
  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Tags />}
      title={t(category ? 'finance.categories.editTitle' : 'finance.categories.createTitle')}
      description={t('finance.categories.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="ct-name" label={t('finance.categories.name')} error={errors.name?.message} full>
          <Input id="ct-name" aria-invalid={!!errors.name} {...form.register('name')} />
        </Field>
        <Field id="ct-kind" label={t('finance.tx.form.kind')}>
          <FormSelect id="ct-kind" control={form.control} name="kind" disabled={!!category} options={TX_KINDS.map((k) => ({ value: k, label: t(`finance.kind.${k}`) }))} />
        </Field>
        <Field id="ct-vat" label={t('finance.categories.vatStatus')}>
          <FormSelect id="ct-vat" control={form.control} name="vatStatus" options={VAT_STATUSES.map((s) => ({ value: s, label: t(`finance.vatStatus.${s}`), description: t(`finance.vatStatusHint.${s}`) }))} />
        </Field>
        <Field id="ct-color" label={t('finance.categories.color')} full>
          <Controller
            control={form.control}
            name="color"
            render={({ field }) => (
              <div id="ct-color" className="flex gap-2" role="radiogroup" aria-label={t('finance.categories.color')}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={field.value === n}
                    aria-label={t('finance.categories.colorN', { n })}
                    onClick={() => field.onChange(n)}
                    className={cn('flex size-9 items-center justify-center rounded-full text-white ring-offset-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', field.value === n && 'ring-2 ring-foreground/60')}
                    style={{ background: chartColor(n) }}
                  >
                    {field.value === n ? <Check className="size-4" /> : null}
                  </button>
                ))}
              </div>
            )}
          />
        </Field>
        <CheckRow {...form.register('active')}>{t('finance.categories.activeLabel')}</CheckRow>
      </FormSection>
    </Modal>
  )
}
